/**
 * UserManagement Component
 * Admin panel for managing users with approval workflow
 * Features:
 * - View all users with status indicators
 * - Pending users section with approve/suspend actions
 * - Create, edit, delete users
 * - Role management
 */
import React, { useState, useEffect, useCallback } from 'react';
import api from '../../lib/axios';
import { useAuth } from '../../context/AuthContext';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Users, 
  UserPlus, 
  Edit, 
  Trash2, 
  Shield, 
  User,
  Mail,
  Calendar,
  Loader2,
  AlertCircle,
  CheckCircle,
  XCircle,
  Clock,
  UserCheck,
  UserX,
  Bell
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// Status badge component
const StatusBadge = ({ status }) => {
  const config = {
    active: { label: 'Activo', className: 'bg-green-100 text-green-700 border-green-200', icon: CheckCircle },
    pending: { label: 'Pendiente', className: 'bg-amber-100 text-amber-700 border-amber-200', icon: Clock },
    suspended: { label: 'Suspendido', className: 'bg-red-100 text-red-700 border-red-200', icon: XCircle },
  };
  const { label, className, icon: Icon } = config[status] || config.active;
  
  return (
    <Badge variant="outline" className={`${className} flex items-center gap-1`}>
      <Icon className="w-3 h-3" />
      {label}
    </Badge>
  );
};

// Pending users notification badge (for export)
export const PendingUsersBadge = ({ count, onClick }) => {
  if (!count || count === 0) return null;
  
  return (
    <button
      onClick={onClick}
      className="relative inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-amber-700 bg-amber-100 border border-amber-200 rounded-full hover:bg-amber-200 transition-colors"
      data-testid="pending-users-badge"
    >
      <Bell className="w-3 h-3" />
      {count} pendiente{count > 1 ? 's' : ''}
    </button>
  );
};

const UserManagement = () => {
  const { isAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    role: 'user'
  });
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('all');

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`${API}/api/auth/users`);
      const allUsers = response.data.users || [];
      setUsers(allUsers);
      setPendingUsers(allUsers.filter(u => u.status === 'pending'));
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAdmin) {
      fetchUsers();
    }
  }, [isAdmin, fetchUsers]);

  // Approve a pending user
  const handleApproveUser = async (userId, userEmail) => {
    try {
      await api.post(`${API}/api/admin/users/${userId}/approve`);
      toast.success(`Usuario ${userEmail} aprobado`);
      fetchUsers();
    } catch (error) {
      console.error('Error approving user:', error);
      toast.error(error.response?.data?.detail || 'Error al aprobar usuario');
    }
  };

  // Suspend a user
  const handleSuspendUser = async (userId, userEmail) => {
    if (!window.confirm(`¿Suspender al usuario ${userEmail}? No podrá acceder al sistema.`)) {
      return;
    }
    try {
      await api.post(`${API}/api/admin/users/${userId}/suspend`);
      toast.success(`Usuario ${userEmail} suspendido`);
      fetchUsers();
    } catch (error) {
      console.error('Error suspending user:', error);
      toast.error(error.response?.data?.detail || 'Error al suspender usuario');
    }
  };

  // Reactivate a suspended user
  const handleReactivateUser = async (userId, userEmail) => {
    try {
      await api.post(`${API}/api/admin/users/${userId}/approve`);
      toast.success(`Usuario ${userEmail} reactivado`);
      fetchUsers();
    } catch (error) {
      console.error('Error reactivating user:', error);
      toast.error(error.response?.data?.detail || 'Error al reactivar usuario');
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post(`${API}/api/admin/users`, formData);
      toast.success('Usuario creado exitosamente');
      setShowAddModal(false);
      setFormData({ email: '', password: '', name: '', role: 'user' });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear usuario');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const updateData = { name: formData.name, role: formData.role };
      if (formData.password) {
        updateData.password = formData.password;
      }
      await api.put(`${API}/api/admin/users/${editingUser.id}`, updateData);
      toast.success('Usuario actualizado');
      setShowEditModal(false);
      setEditingUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar usuario');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteUser = async (userId, userEmail) => {
    if (!window.confirm(`¿Eliminar al usuario ${userEmail}? Esta acción no se puede deshacer.`)) {
      return;
    }
    try {
      await api.delete(`${API}/api/admin/users/${userId}`);
      toast.success('Usuario eliminado');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar usuario');
    }
  };

  const openEditModal = (user) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      password: '',
      name: user.name,
      role: user.role
    });
    setShowEditModal(true);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString('es-PE', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  if (!isAdmin) {
    return (
      <Card className="border-amber-200 bg-amber-50">
        <CardContent className="py-8 text-center">
          <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <p className="text-amber-700">Solo los administradores pueden gestionar usuarios.</p>
        </CardContent>
      </Card>
    );
  }

  // Filter users by tab
  const activeUsers = users.filter(u => u.status === 'active' || !u.status);
  const suspendedUsers = users.filter(u => u.status === 'suspended');

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Gestión de Usuarios
              {pendingUsers.length > 0 && (
                <Badge variant="destructive" className="ml-2 animate-pulse">
                  {pendingUsers.length} pendiente{pendingUsers.length > 1 ? 's' : ''}
                </Badge>
              )}
            </CardTitle>
            <CardDescription>
              Administrar cuentas, roles y aprobaciones de usuario
            </CardDescription>
          </div>
          <Button 
            onClick={() => {
              setFormData({ email: '', password: '', name: '', role: 'user' });
              setShowAddModal(true);
            }}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="add-user-btn"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Nuevo Usuario
          </Button>
        </div>
      </CardHeader>
      
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        ) : (
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-4">
              <TabsTrigger value="all" className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                Todos ({users.length})
              </TabsTrigger>
              <TabsTrigger value="pending" className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Pendientes
                {pendingUsers.length > 0 && (
                  <Badge variant="destructive" className="ml-1 h-5 w-5 p-0 flex items-center justify-center text-xs">
                    {pendingUsers.length}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="active" className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                Activos ({activeUsers.length})
              </TabsTrigger>
              <TabsTrigger value="suspended" className="flex items-center gap-2">
                <XCircle className="w-4 h-4" />
                Suspendidos ({suspendedUsers.length})
              </TabsTrigger>
            </TabsList>

            {/* Pending Users Tab */}
            <TabsContent value="pending">
              {pendingUsers.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <UserCheck className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p className="font-medium">No hay usuarios pendientes</p>
                  <p className="text-sm">Todos los usuarios han sido procesados</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {pendingUsers.map((user) => (
                    <div
                      key={user.id}
                      className="flex items-center justify-between p-4 bg-amber-50 border border-amber-200 rounded-lg"
                      data-testid={`pending-user-${user.email}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-amber-200 flex items-center justify-center">
                          <User className="w-5 h-5 text-amber-700" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{user.name}</p>
                          <p className="text-sm text-slate-500 flex items-center gap-1">
                            <Mail className="w-3 h-3" />
                            {user.email}
                          </p>
                          <p className="text-xs text-slate-400 flex items-center gap-1 mt-1">
                            <Calendar className="w-3 h-3" />
                            Registrado: {formatDate(user.created_at)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          onClick={() => handleApproveUser(user.id, user.email)}
                          className="bg-green-600 hover:bg-green-700"
                          size="sm"
                          data-testid={`approve-user-${user.email}`}
                        >
                          <UserCheck className="w-4 h-4 mr-1" />
                          Aprobar
                        </Button>
                        <Button
                          onClick={() => handleDeleteUser(user.id, user.email)}
                          variant="outline"
                          className="text-red-600 border-red-200 hover:bg-red-50"
                          size="sm"
                          data-testid={`reject-user-${user.email}`}
                        >
                          <UserX className="w-4 h-4 mr-1" />
                          Rechazar
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* All Users Tab */}
            <TabsContent value="all">
              <UsersTable 
                users={users} 
                onEdit={openEditModal}
                onDelete={handleDeleteUser}
                onSuspend={handleSuspendUser}
                onReactivate={handleReactivateUser}
                formatDate={formatDate}
              />
            </TabsContent>

            {/* Active Users Tab */}
            <TabsContent value="active">
              <UsersTable 
                users={activeUsers} 
                onEdit={openEditModal}
                onDelete={handleDeleteUser}
                onSuspend={handleSuspendUser}
                onReactivate={handleReactivateUser}
                formatDate={formatDate}
              />
            </TabsContent>

            {/* Suspended Users Tab */}
            <TabsContent value="suspended">
              {suspendedUsers.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <CheckCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p className="font-medium">No hay usuarios suspendidos</p>
                </div>
              ) : (
                <UsersTable 
                  users={suspendedUsers} 
                  onEdit={openEditModal}
                  onDelete={handleDeleteUser}
                  onSuspend={handleSuspendUser}
                  onReactivate={handleReactivateUser}
                  formatDate={formatDate}
                />
              )}
            </TabsContent>
          </Tabs>
        )}
      </CardContent>

      {/* Add User Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5" />
              Crear Nuevo Usuario
            </DialogTitle>
            <DialogDescription>
              El usuario será creado con estado activo (sin necesidad de aprobación)
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddUser} className="space-y-4">
            <div>
              <Label htmlFor="name">Nombre</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                required
                data-testid="user-name-input"
              />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                required
                data-testid="user-email-input"
              />
            </div>
            <div>
              <Label htmlFor="password">Contraseña</Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                required
                minLength={8}
                data-testid="user-password-input"
              />
            </div>
            <div>
              <Label htmlFor="role">Rol</Label>
              <Select value={formData.role} onValueChange={(v) => setFormData({...formData, role: v})}>
                <SelectTrigger data-testid="user-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">
                    <span className="flex items-center gap-2">
                      <User className="w-4 h-4" />
                      Usuario
                    </span>
                  </SelectItem>
                  <SelectItem value="admin">
                    <span className="flex items-center gap-2">
                      <Shield className="w-4 h-4" />
                      Administrador
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowAddModal(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={submitting} data-testid="create-user-submit">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Crear Usuario
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit User Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5" />
              Editar Usuario
            </DialogTitle>
            <DialogDescription>
              Modificar información del usuario {editingUser?.email}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditUser} className="space-y-4">
            <div>
              <Label htmlFor="edit-name">Nombre</Label>
              <Input
                id="edit-name"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                required
              />
            </div>
            <div>
              <Label htmlFor="edit-email">Email</Label>
              <Input
                id="edit-email"
                type="email"
                value={formData.email}
                disabled
                className="bg-slate-100"
              />
            </div>
            <div>
              <Label htmlFor="edit-password">Nueva Contraseña (dejar vacío para mantener)</Label>
              <Input
                id="edit-password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                minLength={8}
                placeholder="••••••••"
              />
            </div>
            <div>
              <Label htmlFor="edit-role">Rol</Label>
              <Select value={formData.role} onValueChange={(v) => setFormData({...formData, role: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">
                    <span className="flex items-center gap-2">
                      <User className="w-4 h-4" />
                      Usuario
                    </span>
                  </SelectItem>
                  <SelectItem value="admin">
                    <span className="flex items-center gap-2">
                      <Shield className="w-4 h-4" />
                      Administrador
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowEditModal(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Guardar Cambios
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

// Reusable Users Table Component
const UsersTable = ({ users, onEdit, onDelete, onSuspend, onReactivate, formatDate }) => {
  if (users.length === 0) {
    return (
      <div className="text-center py-12 text-slate-500">
        <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
        <p>No hay usuarios en esta categoría</p>
      </div>
    );
  }

  return (
    <div className="border rounded-lg overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="bg-slate-50">
            <TableHead>Usuario</TableHead>
            <TableHead>Estado</TableHead>
            <TableHead>Rol</TableHead>
            <TableHead>Registrado</TableHead>
            <TableHead className="text-right">Acciones</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {users.map((user) => (
            <TableRow key={user.id} data-testid={`user-row-${user.email}`}>
              <TableCell>
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    user.role === 'admin' ? 'bg-purple-100' : 'bg-slate-100'
                  }`}>
                    {user.role === 'admin' ? (
                      <Shield className="w-4 h-4 text-purple-600" />
                    ) : (
                      <User className="w-4 h-4 text-slate-600" />
                    )}
                  </div>
                  <div>
                    <p className="font-medium">{user.name}</p>
                    <p className="text-sm text-slate-500">{user.email}</p>
                  </div>
                </div>
              </TableCell>
              <TableCell>
                <StatusBadge status={user.status || 'active'} />
              </TableCell>
              <TableCell>
                <Badge variant={user.role === 'admin' ? 'default' : 'secondary'}>
                  {user.role === 'admin' ? 'Admin' : 'Usuario'}
                </Badge>
              </TableCell>
              <TableCell className="text-sm text-slate-500">
                {formatDate(user.created_at)}
              </TableCell>
              <TableCell className="text-right">
                <div className="flex items-center justify-end gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onEdit(user)}
                    className="h-8 w-8 p-0"
                    title="Editar"
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  {user.status === 'suspended' ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onReactivate(user.id, user.email)}
                      className="h-8 w-8 p-0 text-green-600 hover:text-green-700 hover:bg-green-50"
                      title="Reactivar"
                    >
                      <UserCheck className="w-4 h-4" />
                    </Button>
                  ) : user.status !== 'pending' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onSuspend(user.id, user.email)}
                      className="h-8 w-8 p-0 text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                      title="Suspender"
                    >
                      <UserX className="w-4 h-4" />
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onDelete(user.id, user.email)}
                    className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                    title="Eliminar"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default UserManagement;
